import {
  Button,
  Col,
  DatePicker,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Tag,
  Alert,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { CardTypeResponse } from '../../model/Card-type.model';
import FeeScheduleResponse from '../../model/Fee-schedule.model';
import { BankResponse } from '../../model/Bank.model';
import { POSDeviceResponse } from '../../model/POS-device.model';
import CardTypeService from '../../service/card-type-service/Card-type.service';
import FeeScheduleService from '../../service/fee-schedule-service/Fee-schedule.service';
import BankService from '../../service/bank-service/Bank.service';
import POSDeviceService from '../../service/pos-device-service/POS-device.service';

const { Text } = Typography;

interface CreateFeeScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRefresh: () => void;
  fetchAllFeeSchedules: () => Promise<FeeScheduleResponse[]>;
}

const CreateFeeScheduleModal: React.FC<CreateFeeScheduleModalProps> = ({
  isOpen,
  onClose,
  onRefresh,
  fetchAllFeeSchedules,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [cardTypes, setCardTypes] = useState<CardTypeResponse[]>([]);
  const [banks, setBanks] = useState<BankResponse[]>([]);
  const [posDevices, setPosDevices] = useState<POSDeviceResponse[]>([]);
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cardTypesData, banksData, posDevicesData] = await Promise.all([
          CardTypeService.getCardTypeList(),
          BankService.getBankList(),
          POSDeviceService.getPOSDeviceList(),
        ]);
        setCardTypes(cardTypesData);
        setBanks(banksData);
        setPosDevices(posDevicesData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Có lỗi xảy ra khi tải dữ liệu!');
      }
    };
    fetchData();
  }, []);

  const handleBankChange = (value: number | null) => {
    setSelectedBankId(value);
  };

  const handleSubmit = async (values: any) => {
    submitForm(values);
  };

  const submitForm = async (values: any) => {
    setLoading(true);
    try {
      console.log('Form values:', values);

      if (!values.posDeviceId) {
        toast.error('Vui lòng chọn thiết bị POS');
        setLoading(false);
        return;
      }

      const formData = {
        feeRate: values.feeRate,
        effectiveDate: values.effectiveDate.format('YYYY-MM-DD'),
        cardTypeId: values.cardTypeId,
        posDeviceId: values.posDeviceId,
        bankId: values.bankId,
      };

      console.log('Sending data to API:', formData);
      await FeeScheduleService.createFeeSchedule(formData);
      toast.success('Tạo biểu phí thành công!');
      onRefresh();
      onClose();
    } catch (error: any) {
      console.error('Error creating fee schedule:', error);
      toast.error(error.message || 'Có lỗi xảy ra khi tạo biểu phí!');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title="Tạo biểu phí"
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={900}
      maskClosable={false}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="mt-4"
        initialValues={{
          effectiveDate: dayjs(),
          feeRate: 0,
        }}
      >
        <Alert
          message="Vui lòng chọn thiết bị POS, loại thẻ và ngân hàng (nếu cần) để tạo biểu phí"
          type="info"
          showIcon
          className="mb-4"
        />

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="posDeviceId"
              label="Thiết bị POS"
              rules={[
                { required: true, message: 'Vui lòng chọn thiết bị POS' },
              ]}
            >
              <Select placeholder="Chọn thiết bị POS">
                {posDevices.map((device) => (
                  <Select.Option key={device.posId} value={device.posId}>
                    {device.deviceCode}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="cardTypeId"
              label={<span>Loại thẻ</span>}
              rules={[{ required: true, message: 'Vui lòng chọn loại thẻ' }]}
            >
              <Select placeholder="Chọn loại thẻ">
                {cardTypes.map((cardType) => (
                  <Select.Option
                    key={cardType.cardTypeId}
                    value={cardType.cardTypeId}
                  >
                    <div className="flex items-center justify-between w-full">
                      <span>{cardType.name}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item name="bankId" label="Ngân hàng">
              <Select
                placeholder="Chọn ngân hàng (tùy chọn)"
                onChange={handleBankChange}
                allowClear
              >
                {banks.map((bank) => (
                  <Select.Option key={bank.id} value={bank.id}>
                    {bank.name}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item
              name="feeRate"
              label={<span>Mức phí (%)</span>}
              rules={[
                { required: true, message: 'Vui lòng nhập mức phí' },
                { type: 'number', min: 0, message: 'Mức phí không được âm' },
              ]}
            >
              <InputNumber
                className="w-full"
                placeholder="Nhập mức phí"
                step={0.01}
                precision={2}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item
              name="effectiveDate"
              label={<span>Ngày hiệu lực</span>}
              rules={[
                { required: true, message: 'Vui lòng chọn ngày hiệu lực' },
              ]}
              tooltip="Biểu phí sẽ có hiệu lực từ ngày này"
            >
              <DatePicker
                className="w-full"
                format="DD/MM/YYYY"
                placeholder="Chọn ngày hiệu lực"
              />
            </Form.Item>
          </Col>
        </Row>

        <div className="flex justify-end gap-2 mt-6">
          <Button onClick={onClose}>Hủy</Button>
          <Button type="primary" htmlType="submit" loading={loading}>
            {loading ? 'Đang xử lý...' : 'Hoàn thành'}
          </Button>
        </div>
      </Form>
    </Modal>
  );
};

export default CreateFeeScheduleModal;
